import React, { Suspense, ComponentType } from 'react';
import { useSiteType } from '@shared/providers/siteType';
import { moduleRegistry, modulePath, DEFAULT_TENANT_KEY } from './ModuleRegistry';

/**
 * Module Loader Component
 *
 * Renders the component registered at `module/component`, preferring the
 * current tenant's override when one exists.
 *
 * The tenant is the site type of the signed-in clinic (`useSiteType`), the same
 * value the router uses to pick the shell and to scope a tenant's own routes.
 * Reading it from the URL instead would disagree with them whenever a clinic is
 * reached through a parent site's segment: the shell would be the tenant's and
 * the screen would be core's.
 *
 * `tenant` overrides that, for the one case `useSiteType` cannot answer: a
 * public screen, rendered before anyone has signed in. There is no clinic to
 * read a site type from, and the route that owns those screens names its
 * segment `:site`, not `:tenant`, so `useSiteType` returns an empty string and
 * every tenant override silently resolves back to the base screen. A caller
 * that already knows which site it is rendering for passes it here. It stays
 * opt-in rather than becoming a second source inside `useSiteType`, because
 * every signed-in caller must keep agreeing with the shell and the tenant
 * routes on one answer.
 *
 * `fallback` covers the tick a registered screen spends suspending — the
 * registry wraps even a base screen in `React.lazy`, so there is always one.
 * It defaults to nothing on purpose: a screen that is already in the bundle
 * resolves on the next tick, and a caption flashed for one frame reads as a
 * glitch (and, written in the developer's language, as the wrong language).
 *
 * `notFound` is what renders when nothing is registered at all. It defaults to
 * nothing as well; a route should pass the same not-found screen it would show
 * for an unknown path, so a wiring bug is not a blank page.
 */

interface ModuleLoaderProps {
  module: string;
  component: string;
  /** Site type to resolve the override for. Only for pre-auth screens. */
  tenant?: string;
  fallback?: React.ReactNode;
  notFound?: React.ReactNode;
  props?: Record<string, unknown>;
}

export const ModuleLoader: React.FC<ModuleLoaderProps> = ({
  module,
  component,
  tenant,
  fallback = null,
  notFound = null,
  props = {},
}) => {
  // Called unconditionally — the explicit tenant only wins afterwards.
  const signedInSiteType = useSiteType();
  const siteType = tenant?.toLowerCase() || signedInSiteType;
  const path = modulePath(module, component);
  const Component = moduleRegistry.getComponent(path, siteType || DEFAULT_TENANT_KEY);

  if (!Component) {
    // A missing registration is a wiring bug, not a user-facing state.
    console.error(`[ModuleLoader] No component registered for "${path}" (tenant: ${siteType})`);
    return <>{notFound}</>;
  }

  return (
    <Suspense fallback={fallback}>
      <Component {...props} />
    </Suspense>
  );
};

/**
 * Higher-order component to wrap a component with tenant override support.
 * Falls back to `BaseComponent` whenever the tenant has no override.
 */
export function withTenantOverride<P extends object>(
  module: string,
  componentName: string,
  BaseComponent: ComponentType<P>
): ComponentType<P> {
  return function TenantAwareComponent(props: P) {
    const siteType = useSiteType();
    const tenantKey = siteType || DEFAULT_TENANT_KEY;
    const path = modulePath(module, componentName);

    if (!moduleRegistry.hasTenantOverride(path, tenantKey)) {
      return <BaseComponent {...props} />;
    }

    const OverrideComponent = moduleRegistry.getComponent(path, tenantKey);
    if (!OverrideComponent) {
      return <BaseComponent {...props} />;
    }

    return (
      <Suspense fallback={<BaseComponent {...props} />}>
        <OverrideComponent {...(props as Record<string, unknown>)} />
      </Suspense>
    );
  };
}

export default ModuleLoader;
